import { PropsWithChildren } from 'react';
import { twMerge } from 'tailwind-merge';

import Skeleton from '@/components/ui/custom/Skeleton';
import { card } from '@/styles/styles';
import CoursePathCardImage from './CoursePathCardImage';
import CoursePathCardHeader from './CoursePathCardHeader';
import CoursePathCardBody from './CoursePathCardBody';
import CoursePathCardActions from './CoursePathCardActions';
import CoursePathCardFooter from './CoursePathCardFooter';
import CoursePathCardProgress from './CoursePathCardProgress';
import CoursePathCardTitle from './CoursePathCardTitle';
import CoursePathCardFormats from './CoursePathCardFormats';
import CoursePathProgressIcon from './CoursePathProgressIcon';
import CoursePathProgressStats from './CoursePathProgressStats';
import CoursePathVersion from './CoursePathVersion';
import CoursePathCardRating from './CoursePathCardRating';
import ConditionalWrapper from '@/components/ui/custom/ConditionalWrapper';
import { Link } from 'react-router-dom';

type Props = PropsWithChildren & {
  className?: string;
  to?: string;
  onClick?: () => void;
  loading?: boolean;
};

function CoursePathCardRoot(props: Props) {
  const { children, className, loading, to = '', onClick } = props;

  if (loading) {
    return (
      <Skeleton className="card rounded-lg max-w-80 h-72 min-w-[17.875rem] bg-primary-content" />
    );
  }

  return (
    <ConditionalWrapper
      condition={!!to}
      wrapper={<Link to={to} role="link" data-testid="courseCardLink" />}
    >
      <li
        className={twMerge(
          card,
          'card rounded-lg max-w-[21rem] aspect-video p-2 gap-2.5 shadow-none',
          className,
        )}
        onClick={onClick}
      >
        {children}
      </li>
    </ConditionalWrapper>
  );
}

type CoursePathCardType = typeof CoursePathCardRoot & {
  Image: typeof CoursePathCardImage;
  Header: typeof CoursePathCardHeader;
  Body: typeof CoursePathCardBody;
  Actions: typeof CoursePathCardActions;
  Footer: typeof CoursePathCardFooter;
  Progress: typeof CoursePathCardProgress;
  Title: typeof CoursePathCardTitle;
  Formats: typeof CoursePathCardFormats;
  ProgressIcon: typeof CoursePathProgressIcon;
  ProgressStats: typeof CoursePathProgressStats;
  Version: typeof CoursePathVersion;
  Rating: typeof CoursePathCardRating;
};

export const CourseCard = CoursePathCardRoot as CoursePathCardType;

CourseCard.Image = CoursePathCardImage;
CourseCard.Header = CoursePathCardHeader;
CourseCard.Body = CoursePathCardBody;
CourseCard.Actions = CoursePathCardActions;
CourseCard.Footer = CoursePathCardFooter;
CourseCard.Progress = CoursePathCardProgress;
CourseCard.Title = CoursePathCardTitle;
CourseCard.Formats = CoursePathCardFormats;
CourseCard.ProgressIcon = CoursePathProgressIcon;
CourseCard.ProgressStats = CoursePathProgressStats;
CourseCard.Version = CoursePathVersion;
CourseCard.Rating = CoursePathCardRating;
